import { useEffect, useState, useRef, useMemo } from 'react';
import { grey } from '@mui/material/colors';
import { Settings } from '@mui/icons-material';
import { Grid, Container, IconButton, SwipeableDrawer, Box, Toolbar, Drawer, useTheme, Typography, Backdrop } from '@mui/material';
import { isMobile, useMobileOrientation } from 'react-device-detect';
import { useDispatch, useSelector } from "react-redux"
import { getData, setTimestamp, setInitialFooterHeight, setAccessMonitoringCameraId } from "../../api/dataSlice"
import { buildLastEventByDay, databaseDateToUtc, DAY_REGEX, isSameTimestamp, normalizeToAvailableTimestamp, timestampToIso, useWindowDimensions } from "../Utilities"
import { useTranslation } from 'react-i18next';
import { useGetAccessMonitoringQuery } from '../../api/graphqlApi';
import TimeSelector from '../archive/TimeSelector'
import CameraSelection from '../CameraSelection';
import DatePicker from '../DatePicker';
import dayjs from 'dayjs';
import VehicleStats from './VehicleStats';

export default function AccessMonitoringControls(props) {

    const { initialSiderWidth, onOpenSideDrawer, setPaddingFooter, drawerBleeding } = props
    const { CAMERAS } = window.conf
    const { t } = useTranslation()
    const { timestamp, accessMonitoringCameraId } = useSelector(getData)
    const { isPortrait, isLandscape } = useMobileOrientation()
    const { appbarHeight } = useWindowDimensions()
    const { data: accessMonitoringData, isLoading: loading } = useGetAccessMonitoringQuery()

    const [footerOpen, setFooterOpen] = useState(false)
    const [footerHeight, setFooterHeight] = useState(48)
    const [pickerOpen, setPickerOpen] = useState(false)
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)
    const [siderWidth, setSiderWidth] = useState()

    const refSider = useRef()
    const dispatch = useDispatch()
    const paddingOffset = 32 // 16px padding on each side
    const startYRef = useRef(0)
    const isPullingRef = useRef(false)
    const theme = useTheme()
    const closedFooterHeight = footerOpen
        ? footerHeight + 189 // depending on footer elements (picker, divider, searchbar, no-data display)
        : footerHeight
    const fullHeight = isPortrait ? "100vh" : `calc(100vh - ${appbarHeight}px)`
    const timestampRef = useRef(timestamp)
    const moreThanOneCam = CAMERAS.length > 1

    // #region logic

    const availableCameras = useMemo( // stable ref cause used in useEffect dependency array
        () => CAMERAS
            .filter(elem => (accessMonitoringData?.byCameraId?.[elem?.id]?.length || 0) > 0) // only include cameras that have data
            .map(elem => elem.id), // extract ids
        [accessMonitoringData, CAMERAS]
    )

    const setSelectedCameraId = (id) => dispatch(setAccessMonitoringCameraId(id === "" ? null : id))

    const activeCameraId = accessMonitoringCameraId ?? availableCameras?.[0] ?? null

    const enabledDaysForCam = useMemo(
        () => accessMonitoringData?.daysByCameraId?.[activeCameraId] || [],
        [accessMonitoringData, activeCameraId]
    )

    const { minDate, maxDate } = useMemo(() => {
        if (enabledDaysForCam.length === 0) return { minDate: null, maxDate: null }
        return {
            minDate: dayjs(enabledDaysForCam[0]),
            maxDate: dayjs(enabledDaysForCam[enabledDaysForCam.length - 1])
        }
    }, [enabledDaysForCam])

    // all event timestamps accurated to second per active camera
    const eventTimestamps = useMemo(() => {
        if (activeCameraId == null) return []

        const items = accessMonitoringData?.byCameraId?.[activeCameraId] || []
        const list = items
            .map(item => item?.timestamp ? databaseDateToUtc(item.timestamp) : null)
            .filter(Boolean)

        return Array.from(new Set(list)).sort((a, b) => Date.parse(a) - Date.parse(b)) // unique + sort ASC
    }, [accessMonitoringData, activeCameraId])

    const lastEventByDay = useMemo(
        () => buildLastEventByDay(eventTimestamps),
        [eventTimestamps]
    )

    const effectiveTimestamp = useMemo(
        () => normalizeToAvailableTimestamp(timestamp, eventTimestamps, lastEventByDay) ?? timestamp,
        [timestamp, eventTimestamps, lastEventByDay] // eslint-disable-line react-hooks/exhaustive-deps
    )

    const currentDay = useMemo(() => {
        if (!effectiveTimestamp) return null
        if (typeof effectiveTimestamp === 'string' && DAY_REGEX.test(effectiveTimestamp)) return effectiveTimestamp
        const iso = timestampToIso(effectiveTimestamp)
        return iso ? iso.slice(0, 10) : null
    }, [effectiveTimestamp])

    const currentDayIndex = useMemo(() => {
        if (!currentDay || enabledDaysForCam.length === 0) return -1
        return enabledDaysForCam.indexOf(currentDay)
    }, [currentDay, enabledDaysForCam])

    const dayEventsForStats = useMemo(() => {
        if (activeCameraId === null || !currentDay) return []
        const items = accessMonitoringData?.byCameraId?.[activeCameraId] ?? []
        return items.filter(elem => typeof elem?.timestamp === "string" && elem.timestamp.slice(0, 10) === currentDay)
    }, [accessMonitoringData, activeCameraId, currentDay])    

    const canStepLeft = currentDayIndex > 0
    const canStepRight = currentDayIndex >= 0 && currentDayIndex < enabledDaysForCam.length - 1

    const updateTimestamp = (value) => {
        const isoTimestamp = timestampToIso(value)
        if (!isoTimestamp) return

        const normalizedTimestamp = normalizeToAvailableTimestamp(isoTimestamp, eventTimestamps, lastEventByDay) ?? isoTimestamp
        if (isSameTimestamp(normalizedTimestamp, timestamp)) return

        dispatch(setTimestamp(normalizedTimestamp))
    }

    function stepLeft() {
        if (!canStepLeft) return
        updateTimestamp(enabledDaysForCam[currentDayIndex - 1])
    }

    function stepRight() {
        if (!canStepRight) return
        updateTimestamp(enabledDaysForCam[currentDayIndex + 1])
    }

    function shouldDisableDate(date) {
        const day = date?.format?.('YYYY-MM-DD')
        return !day || !enabledDaysForCam.includes(day)
    }

    // set initial selected camera
    useEffect(() => {
        if (!availableCameras?.length) return
        if (accessMonitoringCameraId == null || !availableCameras.includes(accessMonitoringCameraId)) {
            setSelectedCameraId(availableCameras[0])
        }
    }, [availableCameras, accessMonitoringCameraId]) // eslint-disable-line react-hooks/exhaustive-deps

    // keep latest timestamp without forcing effects to depend on it
    useEffect(() => {
        timestampRef.current = timestamp
    }, [timestamp])
    
    // set timestamp to "latest event" once camera/data is available
    useEffect(() => {
        if (activeCameraId == null || eventTimestamps.length === 0) return

        const lastEvent = eventTimestamps[eventTimestamps.length - 1]
        if (!lastEvent) return

        if (!isSameTimestamp(lastEvent, timestampRef.current)) {
            updateTimestamp(lastEvent)
        }
    }, [activeCameraId, eventTimestamps]) // eslint-disable-line react-hooks/exhaustive-deps

    // #endregion logic

    // #region layout

    const toggleSideDrawer = (open) => {
        setIsDrawerOpen(open)
        onOpenSideDrawer(open)
        if (open) document.body.style.overflow = 'auto' // keep background (content) scrollable
    }

    const toggleBottomDrawer = (component) => {
        if (component) {
            setFooterHeight(20)
            setFooterOpen(true)
        } else {
            setTimeout(() => {
                setFooterHeight(48);
                setFooterOpen(false);
            }, theme.transitions.duration.leavingScreen);
        }
    }

    const handleTouchStart = (e) => {
        startYRef.current = e.touches[0].clientY
        isPullingRef.current = e.target.closest(".puller") !== null
    }

    const handleTouchMove = (e) => {
        const deltaY = e.touches[0].clientY - startYRef.current

        if (isPullingRef.current && deltaY > 30) {
            toggleBottomDrawer(false)
        }
    }

    useEffect(() => {
        setPaddingFooter(closedFooterHeight)
    }, [closedFooterHeight, setPaddingFooter])

    useEffect(() => {
        setFooterHeight(48)
        dispatch(setInitialFooterHeight(48))
    }, [dispatch])

    useEffect(() => {
        if (footerOpen) {
            document.body.style.overflow = "hidden"
        } else {
            document.body.style.overflow = "auto"
        }
        return () => document.body.style.overflow = "auto"
    }, [footerOpen])

    useEffect(() => {
        return () => {
            onOpenSideDrawer(false)
            setIsDrawerOpen(false)
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps  

    useEffect(() => {
        const current = refSider.current
        if (!current) return

        const observer = new ResizeObserver(() => {
            const newWidth = current.clientWidth - paddingOffset // current sider width without padding offset
            setSiderWidth((prevWidth) => (prevWidth !== newWidth ? newWidth : prevWidth)) // only update if the new width is different from the current state
        })

        observer.observe(current)
        return () => observer.disconnect()
    }, [])

    // #endregion logic

    const controlsAccessMonitoring = (
        <Box sx={{
            mt: 2, pr: 2,
            ...(!isMobile && { pl: 2 }),
            ...(isMobile && isPortrait && { pb: 2, pl: 2, mt: 0.5 })
        }}>

            <Typography
                variant="h5"
                align='center'
                gutterBottom
                sx={{ userSelect: 'none' }}
            >
                {t("pages.access.title")}
            </Typography>

            <Typography
                variant="body2"
                gutterBottom
                pb={2}
                sx={{ userSelect: 'none' }}
            >
                {t("pages.access.description")}
            </Typography>

           {moreThanOneCam && (
                <CameraSelection
                    value={activeCameraId ?? ""}
                    onChange={setSelectedCameraId}
                    availableCameras={availableCameras}
                    disabled={availableCameras?.length === 0}
                />
            )}

            <DatePicker
                timestamp={effectiveTimestamp}
                setTimestamp={updateTimestamp}
                loading={loading}
                shouldDisableDate={shouldDisableDate}
                siderWidth={siderWidth}
                extended={false}
                minDate={minDate}
                maxDate={maxDate}
                open={pickerOpen}
                setOpen={setPickerOpen}
                onSelected={() => {
                    if (isMobile && isPortrait) {
                        setPickerOpen(false)
                        toggleBottomDrawer(false)
                    }
                }}
            />

            <VehicleStats events={dayEventsForStats} />

        </Box>
    )

    // mobile footer portrait
    if (isMobile && isPortrait) {
        return (
            <Container sx={{ backgroundColor: "black", height: "100%" }}>

                {/* grays out background when footer open */}

                <Backdrop
                    open={footerOpen}
                    onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        toggleBottomDrawer(false)
                    }}
                />

                <SwipeableDrawer
                    anchor="bottom"
                    open={footerOpen}
                    onClose={() => toggleBottomDrawer(false)}
                    onOpen={() => toggleBottomDrawer("access")}
                    swipeAreaWidth={drawerBleeding + 9} // increases the area from which swiping is possible
                    disableSwipeToOpen={true} // prevent opening by swiping because its opened by buttons
                    ModalProps={{
                        keepMounted: true,
                        closeAfterTransition: true,
                        disableRestoreFocus: true,
                        hideBackdrop: true
                    }}
                    PaperProps={{
                        sx: {
                            height: `calc(${footerHeight} - ${drawerBleeding}px)`,
                            overflow: 'visible'
                        },
                        onTouchStart: footerOpen ? handleTouchStart : undefined,
                        onTouchMove: footerOpen ? handleTouchMove : undefined
                    }}
                    variant={footerOpen ? "persistent" : "temporary"}
                >

                    <Box
                        sx={{
                            position: 'absolute',
                            top: -footerHeight,
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                            visibility: 'visible',
                            right: 0,
                            left: 0,
                            backgroundColor: "white",
                        }}
                    >

                        {/* puller component to pull out the drawer */}

                        {footerOpen ? (
                            <Box
                                className="puller"
                                sx={{
                                    width: "100%",
                                    height: 24,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center"
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Box
                                    sx={{
                                        width: 30,
                                        height: 6,
                                        backgroundColor: grey[300],
                                        borderRadius: 3
                                    }}
                                />
                            </Box>
                        ) : (
                            <Grid
                                container
                                columns={{ xs: 1 }}
                                sx={{ height: footerOpen ? "inherit" : "unset" }}
                            >
                                <Grid
                                    item
                                    xs={1}
                                    sx={{ height: footerOpen ? "inherit" : "unset" }}
                                >
                                    <Box sx={{
                                        position: "relative",
                                        width: "100%",
                                        display: "flex",
                                        justifyContent: "center"
                                    }}>
                                    
                                        <DatePicker
                                            timestamp={effectiveTimestamp}
                                            setTimestamp={updateTimestamp}
                                            loading={loading}
                                            shouldDisableDate={shouldDisableDate}
                                            siderWidth={siderWidth}
                                            extended={false}
                                            open={pickerOpen}
                                            setOpen={setPickerOpen}
                                            onSelected={() => {
                                                setPickerOpen(false)
                                                toggleBottomDrawer(false)
                                            }}
                                            minDate={minDate}
                                            maxDate={maxDate}
                                            customRender={({ inputRef }) => (
                                                <TimeSelector
                                                    stepLeft={stepLeft}
                                                    timestamp={effectiveTimestamp}
                                                    stepRight={stepRight}
                                                    showDate={true}
                                                    canStepLeft={canStepLeft}
                                                    canStepRight={canStepRight}
                                                    loading={loading}
                                                    setPickerOpen={setPickerOpen}
                                                    inputRef={inputRef}
                                                    showTime={false}
                                                />
                                            )}
                                        />

                                        <Box sx={{
                                            position: "absolute",
                                            right: 0,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            pointerEvents: "none"
                                        }}>
                                            <IconButton
                                                sx={{ p: 1, pointerEvents: "all" }}
                                                onClick={() => toggleBottomDrawer("access")}
                                                color="primary"
                                                disabled={loading}
                                            >
                                                <Settings sx={{ fontSize: "1.66rem" }} />
                                            </IconButton>
                                        </Box>
                                    
                                    </Box>

                                </Grid>

                            </Grid>
                        )}

                    </Box>

                    {/* content of the drawer below puller component */}

                    {footerOpen && (
                        <Box
                            sx={{
                                maxHeight: `calc(${fullHeight} - ${drawerBleeding}px)`,
                                overflowY: 'auto',
                                width: 'auto',
                                overflowX: 'hidden',
                                mt: "4px",
                                transition: `opacity ${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeInOut}`
                            }}
                        >
                            {controlsAccessMonitoring}
                        </Box>
                    )}

                </SwipeableDrawer>
            </Container>
        )

    // mobile sider landscape
    } else if (isMobile && isLandscape) {
        return (
            <SwipeableDrawer
                key={initialSiderWidth} // avoid hiding bleed by forcing remount
                anchor="right"
                open={isDrawerOpen}
                onClose={() => toggleSideDrawer(false)}
                onOpen={() => toggleSideDrawer(true)}
                swipeAreaWidth={drawerBleeding + 9} // increases the area from which swiping is possible
                disableSwipeToOpen={false}
                hideBackdrop={true} // prevent the background from graying out outside the sider
                sx={{ pointerEvents: "none" }} // disable invisible layer that prevents interaction with the content (= background of the drawer)
                ModalProps={{
                    keepMounted: true,
                    closeAfterTransition: true,
                    disableRestoreFocus: true
                }}
                PaperProps={{
                    sx: {
                        width: initialSiderWidth - drawerBleeding,
                        boxSizing: 'border-box',
                        overflow: 'visible',
                        height: `100%`,
                        pointerEvents: "auto" // necessary to remain interaction with the drawer
                    }
                }}
            >
                <Box
                    sx={{
                        backgroundColor: "white",
                        position: 'absolute',
                        height: '100%',
                        top: 0,
                        right: 0,
                        left: -drawerBleeding,
                        visibility: 'visible',
                        overflow: 'auto',
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        overflowX: "hidden"
                    }}
                >

                    {/* puller component to pull out the drawer */}

                    <Box
                        sx={{
                            position: 'absolute',
                            backgroundColor: isDrawerOpen ? '#e0e0e0' : '#003366',
                            height: 30,
                            width: 6,
                            top: `calc(50% + 8px)`,
                            left: 8,
                            borderRadius: 3
                        }}
                    />

                    {/* content of the drawer below puller component */}

                    <Box
                        sx={{
                            overflow: 'auto',
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            mt: `${appbarHeight}px`,
                            ml: `${drawerBleeding}px`,
                            height: "100%",
                            maxHeight: fullHeight,
                            width: `calc(100% - ${drawerBleeding}px)`
                        }}
                    >
                        {controlsAccessMonitoring}
                    </Box>
                </Box>
            </SwipeableDrawer>
        )

    // desktop sider
    } else {
        return (
            <Drawer
                variant="permanent"
                anchor="right"
                PaperProps={{
                    sx: {
                        width: initialSiderWidth,
                        boxSizing: 'border-box'
                    }
                }}
            >
                <Toolbar /> {/* provides top distance without additional functionality */}

                <Box
                    ref={refSider}
                    sx={{
                        overflow: 'auto',
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        overflowX: "hidden"
                    }}
                >
                    {controlsAccessMonitoring}
                </Box>
            </Drawer>
        )
    }
}